/**
 * Payments — Interac e-Transfer → Jobber collection queue (admin/owner).
 *
 * Surfaces api/etransfer-poll: incoming e-transfers matched to the open Jobber
 * invoice they're paying. Jobber's API can't record payments, so each row is one
 * tap from "Record in Jobber" (which then auto-syncs to QuickBooks). The poller
 * also auto-clears rows whose invoice has since been paid in Jobber.
 */
import { useState, useEffect, useCallback } from "react";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0",
  muted: "#8892a4", red: "#ef4444", redDim: "rgba(239,68,68,.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,.12)", yellow: "#eab308", yellowDim: "rgba(234,179,8,.12)", blue: "#3b82f6", blueDim: "rgba(59,130,246,.12)",
};
const money = (n) => `$${(Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => (n == null ? "" : `${Math.round(Number(n) * 100)}%`);

const STATUS_META = {
  matched:   { label: "Matched",    color: C.green,  bg: C.greenDim },
  multiple:  { label: "Ambiguous",  color: C.yellow, bg: C.yellowDim },
  unmatched: { label: "No match",   color: C.red,    bg: C.redDim },
  recorded:  { label: "Recorded",   color: C.muted,  bg: "rgba(136,146,164,.12)" },
  dismissed: { label: "Dismissed",  color: C.muted,  bg: "rgba(136,146,164,.12)" },
  // A Gemstone raffle ticket payment (api/raffle.js) — not an invoice, nothing
  // to record in Jobber; it is handled on /raffle/admin.
  raffle:    { label: "Raffle ticket", color: C.blue, bg: C.blueDim },
};
const TYPE_LABEL = {
  invoice_ref: "invoice # in memo", name_amount: "name + amount", partial: "partial payment",
  amount_only: "amount only", manual: "manual", none: "—",
};

function Btn({ children, onClick, disabled, kind = "ghost", size = "md" }) {
  const styles = {
    ghost:  { background: "transparent", color: C.text, border: `1px solid ${C.border}` },
    accent: { background: C.accent, color: "#0d0f12", border: `1px solid ${C.accent}`, fontWeight: 700 },
    blue:   { background: C.blueDim, color: C.blue, border: `1px solid ${C.blue}` },
    danger: { background: C.redDim, color: C.red, border: `1px solid rgba(239,68,68,.4)` },
  }[kind];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles, padding: size === "sm" ? "5px 10px" : "8px 14px", borderRadius: 8,
      fontSize: size === "sm" ? 12 : 13, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

// One sender/amount vs matched-client comparison. Highlights name mismatches so a
// coincidental amount-only match (right $, wrong person) is obvious.
function NameCompare({ sender, client, type }) {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
  const senderN = norm(sender), clientN = norm(client);
  const overlap = client && (senderN.includes(clientN) || clientN.includes(senderN) ||
    sender.toLowerCase().split(/\s+/).some((t) => t.length > 2 && clientN.includes(norm(t))));
  const mismatch = client && !overlap && type === "amount_only";
  return (
    <div style={{ fontSize: 12.5, color: C.muted }}>
      <span style={{ color: C.text }}>{sender}</span>
      {client && <> &nbsp;→&nbsp; <span style={{ color: mismatch ? C.yellow : C.text, fontWeight: 600 }}>{client}</span>
        {mismatch && <span style={{ color: C.yellow, marginLeft: 6, fontSize: 11 }}>⚠ name differs — verify</span>}</>}
    </div>
  );
}

function Row({ r, onAction, busy }) {
  const m = STATUS_META[r.match_status] || STATUS_META.unmatched;
  const cands = Array.isArray(r.candidates) ? r.candidates : [];
  const recordedLike = r.match_status === "recorded" || r.match_status === "dismissed" || r.match_status === "raffle";
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, opacity: recordedLike ? 0.65 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{money(r.amount)}</span>
          <span style={{ fontSize: 12, color: C.muted }}>{(r.received_at || r.created_at || "").slice(0, 10)}</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", padding: "3px 10px", borderRadius: 999, background: m.bg, color: m.color }}>
          {m.label}{r.match_confidence != null && r.match_status === "matched" ? ` · ${pct(r.match_confidence)}` : ""}
        </span>
      </div>

      <div style={{ marginTop: 8 }}>
        <NameCompare sender={r.sender_name} client={r.matched_client_name} type={r.match_type} />
        {r.match_status === "matched" && (
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
            Invoice <b style={{ color: C.text }}>#{r.matched_invoice_number}</b> · balance {money(r.matched_invoice_balance)}
            {r.matched_source === "qbo" && <span style={{ color: C.blue, marginLeft: 8, fontWeight: 700 }}>QuickBooks-native</span>}
            {r.is_partial && <span style={{ color: C.yellow, marginLeft: 8, fontWeight: 700 }}>PARTIAL — {money(r.amount)} of {money(r.matched_invoice_balance)}</span>}
            <span style={{ color: C.muted, marginLeft: 8 }}>· {TYPE_LABEL[r.match_type] || r.match_type}</span>
          </div>
        )}
        {r.invoice_hint && <div style={{ fontSize: 11.5, color: C.accent, marginTop: 3 }}>memo cited invoice #{r.invoice_hint}</div>}
        {r.message_text && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, fontStyle: "italic" }}>"{r.message_text}"</div>}
      </div>

      {/* Candidate picker for ambiguous / unmatched */}
      {!recordedLike && (r.match_status === "multiple" || r.match_status === "unmatched") && cands.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Possible invoices — pick the right one:</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {cands.map((c, i) => (
              <div key={c.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px" }}>
                <span style={{ fontSize: 12.5, color: C.text }}>#{c.invoiceNumber} · {c.clientName} · bal {money(c.balance)}
                  {c.amountMatch && <span style={{ color: C.green, marginLeft: 6, fontSize: 11 }}>✓ amount</span>}
                  {c.nameScore >= 0.7 && <span style={{ color: C.accent, marginLeft: 6, fontSize: 11 }}>✓ name</span>}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {c.webUri && <a href={c.webUri} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: C.blue }}>open</a>}
                  <Btn size="sm" onClick={() => onAction("set-invoice", r, { invoiceId: c.id, webUri: c.webUri })} disabled={!!busy}>This one</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions — QBO-native rows record in one click; Jobber rows deep-link. */}
      {!recordedLike && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {r.match_status === "matched" && r.matched_source === "qbo" && (
            <Btn kind="accent" size="sm" onClick={() => onAction("record-qb-payment", r)} disabled={!!busy}>
              {busy === r.id ? "Recording…" : "⚡ Record payment in QuickBooks"}
            </Btn>
          )}
          {r.matched_source === "qbo" && r.qb_invoice_id && (
            <a href={`https://qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(r.qb_invoice_id)}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Btn size="sm">View in QBO ↗</Btn></a>
          )}
          {r.jobber_web_uri && <a href={r.jobber_web_uri} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
            <Btn kind="accent" size="sm">Record in Jobber ↗</Btn></a>}
          {r.match_status === "matched" && <Btn size="sm" kind="blue" onClick={() => onAction("mark-recorded", r)} disabled={!!busy}>{busy === r.id ? "…" : "Mark recorded"}</Btn>}
          <Btn size="sm" onClick={() => onAction("rematch", r)} disabled={!!busy}>Re-match</Btn>
          <Btn size="sm" kind="danger" onClick={() => onAction("dismiss", r)} disabled={!!busy}>Dismiss</Btn>
        </div>
      )}
      {recordedLike && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>
          {r.match_status === "recorded"
            ? `✓ Recorded${r.recorded_by === "auto" ? " automatically (invoice shows paid)" : ` by ${r.recorded_by || "—"}`}${r.qb_payment_id ? ` · QB payment #${r.qb_payment_id}` : ""}`
            : "Dismissed"}
        </div>
      )}
    </div>
  );
}

export default function Payments() {
  const { session } = useSession() || {};
  const perms = usePermissions() || {};
  const { isAdmin, isOwner } = perms;
  const userEmail = session?.user?.email?.toLowerCase() || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);
  const [filter, setFilter] = useState("open"); // open | all | recorded
  const [msg, setMsg] = useState("");

  const api = useCallback((qs, opts) => {
    const sep = qs.includes("?") ? "&" : "?";
    return apiFetch(`/api/etransfer-poll${qs}${sep}userEmail=${encodeURIComponent(userEmail)}`, opts).then((r) => r.json());
  }, [userEmail]);
  const post = useCallback((body) => apiFetch("/api/etransfer-poll", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...body, userEmail }),
  }).then((r) => r.json()), [userEmail]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("?action=list");
      setData(res);
    } catch (e) { setMsg(String(e.message || e)); }
    setLoading(false);
  }, [api]);

  useEffect(() => { if (userEmail) load(); }, [userEmail, load]);

  const runPoll = async () => {
    setPolling(true); setMsg("");
    try {
      const res = await api("?action=poll");
      setMsg(res.ok ? `Scanned ${res.scanned} · ${res.new} new · ${res.cleared || 0} auto-cleared` : `Error: ${res.error}`);
      await load();
    } catch (e) { setMsg(String(e.message || e)); }
    setPolling(false);
  };

  const onAction = async (action, r, extra = {}) => {
    // The QBO write is the one REAL money action here — confirm, and send it
    // through apiFetch so the verified admin JWT rides along (the server gates
    // this action on requireAdmin, not the userEmail param).
    if (action === "record-qb-payment") {
      const partial = r.is_partial ? ` (PARTIAL — balance is ${money(r.matched_invoice_balance)})` : "";
      if (!window.confirm(
        `Record ${money(r.amount)} from ${r.sender_name} against QuickBooks invoice #${r.matched_invoice_number}${partial}?\n\n` +
        `This writes a real payment in QuickBooks (deposited to Undeposited Funds). ` +
        `Reversible by deleting the payment in QBO.`
      )) return;
      setBusy(r.id);
      try {
        const res = await apiFetch("/api/etransfer-poll", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "record-qb-payment", id: r.id }),
        }).then((rr) => rr.json());
        setMsg(res.ok
          ? (res.alreadyPaid
              ? `Invoice #${res.invoiceNumber} was already paid in QuickBooks — row cleared.`
              : `✓ Recorded — QuickBooks payment #${res.paymentId} on invoice #${res.invoiceNumber}${res.balanceAfter > 0 ? ` · ${money(res.balanceAfter)} still owing` : " · paid in full"}`)
          : `Error: ${res.error || "failed"}`);
        await load();
      } catch (e) { setMsg(String(e.message || e)); }
      setBusy(null);
      return;
    }
    setBusy(r.id);
    try {
      if (action === "rematch") await post({ action: "rematch", id: r.id });
      else await post({ action, id: r.id, ...extra });
      await load();
    } catch (e) { setMsg(String(e.message || e)); }
    setBusy(null);
  };

  if (!isAdmin && !isOwner) {
    return <div style={{ padding: 40, color: C.muted, background: C.bg, minHeight: "100vh" }}>Payments is admin-only.</div>;
  }

  const rows = data?.rows || [];
  const counts = data?.counts || {};
  const visible = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "recorded") return r.match_status === "recorded" || r.match_status === "dismissed" || r.match_status === "raffle";
    return r.match_status !== "recorded" && r.match_status !== "dismissed" && r.match_status !== "raffle"; // "open"
  });
  const openCount = (counts.matched || 0) + (counts.multiple || 0) + (counts.unmatched || 0);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, padding: "24px 20px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>💸 e-Transfer Payments</h1>
            <p style={{ color: C.muted, fontSize: 13, margin: "6px 0 0", maxWidth: 560 }}>
              Interac deposits matched to the open invoice they're paying. <b style={{ color: C.text }}>QuickBooks-native</b> invoices record in one click right here; Jobber invoices still tap through to <b style={{ color: C.text }}>Record in Jobber</b> (its API can't write payments — Jobber then syncs to QuickBooks itself). Rows auto-clear once the invoice shows paid.
            </p>
          </div>
          <Btn kind="accent" onClick={runPoll} disabled={polling}>{polling ? "Checking inbox…" : "Check inbox now"}</Btn>
        </div>

        {/* Summary chips */}
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          {[["matched", "Matched"], ["multiple", "Ambiguous"], ["unmatched", "No match"], ["recorded", "Recorded"]].map(([k, label]) => (
            <div key={k} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px", minWidth: 90 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: (STATUS_META[k] || {}).color || C.text }}>{counts[k] || 0}</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          {[["open", `Needs action (${openCount})`], ["recorded", "Done"], ["all", "All"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} style={{
              background: filter === k ? C.accentDim : "transparent", color: filter === k ? C.accent : C.muted,
              border: `1px solid ${filter === k ? C.accent : C.border}`, borderRadius: 999, padding: "5px 14px", fontSize: 12.5, cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>

        {msg && <div style={{ marginTop: 12, fontSize: 12.5, color: C.muted }}>{msg}</div>}

        <div style={{ marginTop: 16 }}>
          {loading ? <div style={{ color: C.muted, padding: 20 }}>Loading…</div>
            : visible.length === 0 ? <div style={{ color: C.muted, padding: 20, textAlign: "center", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
                {filter === "open" ? "Nothing to record — you're all caught up. 🎉" : "No payments here yet."}
              </div>
            : visible.map((r) => <Row key={r.id} r={r} onAction={onAction} busy={busy} />)}
        </div>
      </div>
    </div>
  );
}
